<template>
  <div class="meta-policy">
    <div class="mp-wrap">
      <header class="mp-header">
        <h1>Privacy Policy</h1>
        <p class="mp-sub">
          Platform <strong>Telejurnal</strong> &mdash;
          <a href="https://www.telejurnal.com">www.telejurnal.com</a><br>
          Operator: <strong>Inamed Consult SRL</strong><br>
          Last updated: June 25, 2026
        </p>
      </header>

      <p>
        This Privacy Policy explains how <strong>Inamed Consult SRL</strong>
        ("Telejurnal", "we", "us"), operator of the Telejurnal platform available at
        <a href="https://www.telejurnal.com">www.telejurnal.com</a>, collects, uses, and
        protects your information when you use our web application to manage your
        telecom services, SIM cards, and account.
      </p>
      <p>
        We process personal data in accordance with the EU General Data Protection
        Regulation (GDPR) and applicable national law.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account &amp; contact data:</strong> your name, username, email address, and phone number.</li>
        <li><strong>Billing &amp; contract data:</strong> address, company details (name, fiscal code), and your history of services and payments.</li>
        <li><strong>Service usage data:</strong> associated phone numbers (MSISDN), SIM cards, traffic, options, and subscriptions.</li>
        <li><strong>Technical data:</strong> IP address, device/browser information, and access logs needed to operate and secure the service.</li>
        <li><strong>Data from Facebook Login:</strong> if you choose to sign in with Facebook, your public identifier, name, and email address (when you authorize it).</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To create, authenticate, and secure your account.</li>
        <li>To provide telecom and related services and keep you informed about their status.</li>
        <li>To process billing and payments and meet accounting and tax obligations.</li>
        <li>To provide customer support and respond to your requests.</li>
        <li>To comply with legal obligations and prevent fraud or abuse.</li>
      </ul>

      <h2>3. Facebook Login and the Meta Platform</h2>
      <p>
        When you choose to authenticate or interact with our service through Facebook
        Login or the Meta Graph API, we may receive from Meta Platforms, Inc. &mdash; only
        with your explicit consent &mdash; your public user identifier (Facebook ID), your
        public name, and your email address (if you authorize it). This data is used
        solely to create and authenticate your Telejurnal account. We do not sell this
        data and we do not use Meta-provided data for any purpose other than operating
        this service. You can revoke the application's access at any time from your
        Facebook account settings (<em>Settings &rarr; Apps and Websites</em>).
      </p>

      <h2>4. Sharing your information</h2>
      <p>We share data only with parties that help us run the service:</p>
      <ul>
        <li>Technical service providers (hosting, electronic communications, payment processors).</li>
        <li>The mobile network operator (MVNO) through which telephony services are delivered.</li>
        <li>Public authorities, where required by a legal obligation.</li>
      </ul>
      <p>We do not sell your personal data to third parties.</p>

      <h2>5. Data retention</h2>
      <p>
        We keep your data for as long as your account is active or as needed to provide
        the service and to meet legal, accounting, or reporting requirements.
      </p>

      <h2>6. Your rights</h2>
      <p>
        Under the GDPR you may have the right to access, correct, export, restrict, or
        delete your personal data, to object to processing, and to withdraw consent at
        any time. To exercise these rights, contact us at the address below. You may also
        lodge a complaint with the national data protection authority (ANSPDCP).
      </p>

      <h2>7. Data deletion</h2>
      <p>
        To request deletion of your data, including data obtained through Facebook Login,
        email us at <a href="mailto:office@telejurnal.com">office@telejurnal.com</a> with
        the subject "Data deletion request", or remove the Telejurnal application from your
        Facebook account (<em>Settings &rarr; Apps and Websites</em>) to revoke its access.
        Upon verification we will anonymize or delete your personal data within the
        timeframe required by law, except where retention is legally required.
      </p>

      <h2>8. Security</h2>
      <p>
        We use industry-standard measures (encryption in transit via HTTPS, encrypted
        storage of credentials, and access controls) to protect your information. No
        method of transmission or storage is completely secure, but we work to protect
        your data and review our practices regularly.
      </p>

      <h2>9. Children's privacy</h2>
      <p>
        The service is not directed to children under the age required by local law to
        consent to data processing, and we do not knowingly collect their data without
        appropriate consent.
      </p>

      <h2>10. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The "Last updated" date above
        reflects the latest revision. Material changes will be communicated through the
        service where appropriate.
      </p>

      <h2>11. Contact us</h2>
      <p>For any privacy question or request, contact:</p>
      <ul>
        <li><strong>Company:</strong> Inamed Consult SRL</li>
        <li><strong>Email:</strong> <a href="mailto:office@telejurnal.com">office@telejurnal.com</a></li>
        <li><strong>Web:</strong> <a href="https://www.telejurnal.com">www.telejurnal.com</a></li>
      </ul>

      <footer class="mp-footer">
        &copy; Inamed Consult SRL &mdash; Telejurnal. All rights reserved.
      </footer>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'MetaPolicyView'
})
</script>

<style scoped>
.meta-policy {
  background: #ffffff;
  color: #1a1a1a;
  min-height: 100vh;
}
.mp-wrap {
  max-width: 880px;
  margin: 0 auto;
  padding: 32px 22px 80px;
  font-family: 'Segoe UI', Calibri, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.65;
}
.mp-header {
  border-bottom: 3px solid #1565c0;
  padding-bottom: 18px;
  margin-bottom: 28px;
}
.mp-header h1 {
  font-size: 26px;
  color: #0d47a1;
  margin: 0 0 6px;
}
.mp-sub {
  color: #555;
  font-size: 14px;
}
.meta-policy h2 {
  font-size: 19px;
  color: #1565c0;
  margin-top: 34px;
  margin-bottom: 10px;
  border-left: 4px solid #1565c0;
  padding-left: 10px;
}
.meta-policy p,
.meta-policy li {
  margin: 8px 0;
}
.meta-policy ul {
  padding-left: 22px;
}
.meta-policy a {
  color: #1565c0;
}
.mp-footer {
  margin-top: 50px;
  padding-top: 18px;
  border-top: 1px solid #e0e0e0;
  font-size: 13px;
  color: #666;
}
</style>
